// src/components/ErrorBoundary.jsx
//
// Top-level React error boundary. Required by CLAUDE.md:
//   "ErrorBoundary wraps <AppContext.Provider>. Renders fallback UI
//   with 'Try again'. Do not bypass."
//
// Class component — React error boundaries must be class components
// (getDerivedStateFromError / componentDidCatch are class-only lifecycle
// methods; hooks cannot replicate them).
//
// Placement: wraps <App /> in src/main.jsx so any unhandled render error
// anywhere in the tree is caught here instead of producing a blank screen.

import React from "react";
import { T } from "../app/tokens.js";
import { log } from "../utils/logger.js";

export class ErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidCatch(error, info) {
    log.error("ErrorBoundary caught unhandled render error", { name: error?.name, message: error?.message });

    // Fire-and-forget POST to the server so the crash lands in the audit log
    // and is visible to the team without waiting for a user report.
    // Only error metadata travels — no PII-adjacent fields (location IDs,
    // lease costs, etc.) are included. Network failure is swallowed here; the
    // catch must not re-throw or it would trigger a second boundary catch.
    fetch("/api/errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message:        error?.message        ?? String(error),
        stack:          error?.stack          ?? null,
        componentStack: info?.componentStack  ?? null,
      }),
    }).catch(() => {});
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        data-testid="error-boundary-fallback"
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: T.bg,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            padding: "40px 48px",
            maxWidth: 440,
            textAlign: "center",
            boxShadow: T.shadowPanel,
          }}
        >
          <div style={{ fontSize: T.fs3xl, marginBottom: 16 }}>⚠️</div>
          <h1
            style={{
              fontSize: T.fsLgX,
              fontWeight: T.fwBold,
              color: T.dark,
              margin: "0 0 8px",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: T.fsMd,
              color: T.mid,
              margin: "0 0 24px",
              lineHeight: 1.6,
            }}
          >
            An unexpected error occurred. Reload the page or try again - your
            data has not been affected.
          </p>
          <button
            type="button"
            data-testid="error-boundary-retry"
            onClick={this.handleReset}
            style={{
              background: T.blue,
              color: T.card,
              border: "none",
              borderRadius: 6,
              padding: "10px 24px",
              fontSize: T.fsMd,
              fontWeight: T.fwSemibold,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
